import { SystemProps } from '@chakra-ui/react';
import { useRef, useEffect, RefObject } from 'react';

type HiddenWrapEffect = (_children: HTMLElement[], _container: HTMLElement) => void;
export function useHiddenWrap<T extends HTMLElement>({
   wrappedChildrenEffect,
   shownChildrenEffect,
}: {
   wrappedChildrenEffect?: HiddenWrapEffect;
   shownChildrenEffect?: HiddenWrapEffect;
}): {
   ref: RefObject<T | null>;
   display: SystemProps['display'];
   flexWrap: SystemProps['flexWrap'];
   overflow: SystemProps['overflow'];
} {
   const ref = useRef<T | null>(null);
   const wrappedChildrenEffectRef = useRef(wrappedChildrenEffect);
   const shownChildrenEffectRef = useRef(shownChildrenEffect);

   // Keep refs in sync with latest callbacks
   useEffect(() => {
      wrappedChildrenEffectRef.current = wrappedChildrenEffect;
      shownChildrenEffectRef.current = shownChildrenEffect;
   }, [wrappedChildrenEffect, shownChildrenEffect]);

   useEffect(() => {
      if (!ref?.current) {
         return;
      }

      const container = ref.current;

      const measure = () => {
         onBrowserRender(
            container,
            wrappedChildrenEffectRef.current,
            shownChildrenEffectRef.current
         );
      };

      // Use ResizeObserver to detect size changes
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(container);

      // Also listen for window resize as child layout may change without container size changing
      window.addEventListener('resize', measure);

      return () => {
         resizeObserver.disconnect();
         window.removeEventListener('resize', measure);
      };
   }, []);
   return { ref, display: 'flex', flexWrap: 'wrap', overflow: 'hidden' };
}

function onBrowserRender<T extends HTMLElement>(
   container: T,
   wrappedEffect?: HiddenWrapEffect,
   shownEffect?: HiddenWrapEffect
) {
   // Batch all reads together to minimize reflows
   const containerTop = container.offsetTop;
   const containerHeight = container.offsetHeight;
   const buffer = containerHeight / 2;
   const wrappedPX = containerTop + buffer;

   const children = Array.from(container.children) as HTMLElement[];
   // Read all offsetTop values in one pass
   const childTops = children.map(child => child.offsetTop);

   const wrappedChildren: HTMLElement[] = [];
   const shownChildren: HTMLElement[] = [];

   for (const [i, child] of children.entries()) {
      if (childTops[i] > wrappedPX) {
         wrappedChildren.push(child);
      } else {
         shownChildren.push(child);
      }
   }

   wrappedEffect?.(wrappedChildren, container);
   shownEffect?.(shownChildren, container);
}
